import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Col, Row, Skeleton } from 'antd'

import benefitPlanPeriods, { PlanPeriodDto } from 'core/application/adminDashboard/benefitDetails/benefitPlanPeriods'

import Card from 'common/components/dataDisplay/Card'
import Tabs from 'common/components/dataDisplay/Tabs'
import Alert from 'common/components/feedback/Alert'

import { BenefitPlanPeriodDropDown } from '../../../common/components'
import useQueryParams from '../../../common/hooks/useQueryParams'
import useQueryRequest from '../../../common/hooks/useQueryRequest'
import AllReimbursements from '../common/allReimbursements'
import Transactions from '../common/transactions'
import AllowanceSummary from './allowanceSummary'
import OverReimbursements from './overReimbursements'
import ReimbursementsBalances from './reimbursementsBalances'
import ReimbursementsDue from './reimbursementsDue'
import ReimbursementsHistory from './reimbursementsHistory'

import './Reimbursements.less'

export const REIMBURSEMENTS_PATH = 'reimbursements'

const GroupHraReimbursements: React.FC = () => {
  const { t: translate } = useTranslation()
  const queryParams = useQueryParams()
  const planPeriodIdQueryParam = queryParams.get('planPeriodId')
  const [selectedPlanPeriod, setSelectedPlanPeriod] = useState<PlanPeriodDto | null>()
  const [planPeriods] = useQueryRequest<PlanPeriodDto[]>(null, () => benefitPlanPeriods())

  useEffect(() => {
    if (planPeriods && planPeriods.length > 0) {
      const found = planPeriodIdQueryParam
        ? planPeriods.find((x) => x.id === planPeriodIdQueryParam)
        : planPeriods.find((x) => x.active == true)

      if (found) {
        setSelectedPlanPeriod(found)
      }
    }
  }, [planPeriods])

  return (
    <Skeleton active loading={!planPeriods}>
      <Card
        title={[
          <h5 key="title">{translate('admin-dashboard.reimbursements.reimbursements-due.benefit-summary-title')}</h5>,
          <div key="summary" className="benefit_period">
            <BenefitPlanPeriodDropDown
              defaultValue={planPeriodIdQueryParam}
              planPeriods={planPeriods}
              showStartDateAndEndDate={true}
              onPlanPeriodSelected={(planPeriodId: string) => {
                const found = planPeriods.find((x) => x.id === planPeriodId)
                setSelectedPlanPeriod(found)
              }}
            />
          </div>,
        ]}
        className="reimbursement_due_summary"
      >
        {selectedPlanPeriod && (
          <>
            <Row justify="space-between">
              <Col xs={24} sm={15} md={15} lg={15} xl={15}>
                <AllowanceSummary
                  planPeriodId={selectedPlanPeriod.id}
                  allowanceType={selectedPlanPeriod.allowanceType}
                />
              </Col>
              <Col xs={24} sm={8} md={8} lg={8} xl={8}>
                <Alert
                  type="info"
                  showIcon
                  message={translate('admin-dashboard.reimbursements.reimbursements-due.hippa-privacy-alert-message')}
                  description={translate(
                    'admin-dashboard.reimbursements.reimbursements-due.hippa-privacy-alert-description',
                  )}
                  className="hippa_privacy_notice mb-16"
                />
              </Col>
            </Row>

            <Tabs
              panes={[
                {
                  key: 'reimbursements-due',
                  title: (
                    <h6>{translate('admin-dashboard.reimbursements.reimbursements-due.reimbursements-due-title')}</h6>
                  ),
                  content: <ReimbursementsDue planPeriodId={selectedPlanPeriod.id} />,
                },
                {
                  key: 'history',
                  title: <h6>{translate('admin-dashboard.reimbursements.reimbursements-due.history-title')}</h6>,
                  content: <ReimbursementsHistory planPeriod={selectedPlanPeriod} />,
                },
                {
                  key: 'balances',
                  title: <h6>{translate('admin-dashboard.reimbursements.reimbursements-due.balances-title')}</h6>,
                  content: <ReimbursementsBalances planPeriodId={selectedPlanPeriod.id} />,
                },
                {
                  key: 'over-reimbursements',
                  title: (
                    <h6>{translate('admin-dashboard.reimbursements.reimbursements-due.over-reimbursements-title')}</h6>
                  ),
                  content: <OverReimbursements planPeriodId={selectedPlanPeriod.id} />,
                },
                {
                  key: 'transactions',
                  title: <h6>{translate('admin-dashboard.reimbursements.transactions.title')}</h6>,
                  content: <Transactions planPeriod={selectedPlanPeriod} />,
                },
                {
                  key: 'all-submissions',
                  title: <h6>{translate('admin-dashboard.reimbursements.all-submissions.title')}</h6>,
                  content: <AllReimbursements planPeriodId={selectedPlanPeriod.id} />,
                },
              ]}
            />
          </>
        )}
      </Card>
    </Skeleton>
  )
}

export default GroupHraReimbursements
